import type {
  CppInvoiceNote,
  CppInvoiceTotals,
  CppInvoiceTransaction,
  CppInvoiceTransactionsQuery,
  CppInvoiceTransactionsResponse,
} from '~/services/LuxLoyalty';

/** Largest page the transactions endpoint accepts — exporting uses it to minimise round trips. */
export const EXPORT_PAGE_SIZE = 500;

/**
 * Hard ceiling on an export.
 *
 * A month of a high-volume partner is well within this; the cap exists so a mis-scoped export
 * cannot pull an unbounded number of rows into the browser. Hitting it is reported rather than
 * silently truncating the file.
 */
export const MAX_EXPORT_ROWS = 50000;

/** Progress of an in-flight export. */
export interface ExportProgress {
  fetched: number;
  /** Rows matching the query across all pages, as reported by the endpoint. */
  total: number;
}

/** Everything an export needs from the endpoint. */
export interface FetchAllOptions {
  fetchPage: (
    query: CppInvoiceTransactionsQuery,
    options: { signal?: AbortSignal },
  ) => Promise<CppInvoiceTransactionsResponse>;
  onProgress?: (progress: ExportProgress) => void;
  signal?: AbortSignal;
}

/** The assembled export. */
export interface FetchAllResult {
  transactions: Array<CppInvoiceTransaction>;
  /** The period's currency, for the cost column heading. */
  currency: string | null;
  /** The timezone the service dates rows in, for the CSV's Date/Time columns. */
  timezone: string;
  /** True when {@link MAX_EXPORT_ROWS} stopped the walk before the endpoint ran out of rows. */
  truncated: boolean;
  /** Rows matching the query across all pages, per the endpoint. */
  totalItems: number;
  /** Whole-period figures. Identical on every page, so the first page's copy is enough. */
  totals: CppInvoiceTotals;
  blockers: Array<CppInvoiceNote>;
  invoiceable: boolean;
}

/**
 * Walks every page of the transactions endpoint for one partner and period.
 *
 * The grid is server-paginated at 500 rows, but the activity statement attached to an invoice has
 * to be the whole period, so this pages through until the endpoint reports no more.
 *
 * `fetchPage` is injected rather than imported so the walk — the part that can silently drop rows
 * — is testable without a network or module mock.
 *
 * @param query - Partner and period to export.
 * @param options - Page fetcher, optional progress callback, optional abort signal.
 * @returns Every transaction in the period, capped at {@link MAX_EXPORT_ROWS}.
 */
export async function fetchAllInvoiceTransactions(
  query: Pick<CppInvoiceTransactionsQuery, 'partnerId' | 'start' | 'end' | 'transactionType'>,
  options: FetchAllOptions,
): Promise<FetchAllResult> {
  const { fetchPage, onProgress, signal } = options;

  const transactions: Array<CppInvoiceTransaction> = [];
  let offset = 0;
  // Assigned on the first iteration — the loop body always runs before any `break`.
  let currency: string | null;
  let timezone: string;
  let totalItems: number;
  let totals: CppInvoiceTotals;
  let blockers: Array<CppInvoiceNote>;
  let invoiceable: boolean;
  let truncated = false;

  for (;;) {
    const response = await fetchPage({ ...query, offset, limit: EXPORT_PAGE_SIZE, sort_order: 'asc' }, { signal });

    currency = response.currency;
    timezone = response.timezone;
    totalItems = response.pagination.total_items;
    totals = response.totals;
    blockers = response.blockers;
    invoiceable = response.invoiceable;
    transactions.push(...response.result);
    onProgress?.({ fetched: transactions.length, total: totalItems });

    // An empty page that still claims a successor would loop forever; treat it as the end.
    if (response.result.length === 0 || !response.pagination.has_next) {
      break;
    }

    if (transactions.length >= MAX_EXPORT_ROWS) {
      truncated = true;
      break;
    }

    // Advance by what the server echoed back rather than by our own counter, so a server-clamped
    // limit cannot make the walk skip or repeat rows.
    offset = response.pagination.offset + response.pagination.limit;
  }

  return {
    transactions: transactions.slice(0, MAX_EXPORT_ROWS),
    currency,
    timezone,
    truncated,
    totalItems,
    totals,
    blockers,
    invoiceable,
  };
}
